import {
  __ONLY_WRITE_STATE_FROM_ACTIONS as writeState,
  readState,
} from "@/__main__/app-state.mjs";
import { isPersistent, MAX_TIME } from "@/__main__/constants.mjs";
import { readData } from "@/__main__/get-data.mjs";
import router, { updateRoute } from "@/__main__/router.mjs";
import eventBus from "@/app/app-event-bus.mjs";
import { GAME_SHORT_NAMES } from "@/app/constants.mjs";
import { IS_SEARCH_ACTIVE, SEARCH_QUERY } from "@/search/constants.mjs";
import omniSearchState, { setSearchGame } from "@/search/modal-state.mjs";
import type { SearchDispatch } from "@/search/modal-view.mjs";
import searchRefs from "@/search/refs.mjs";
import type { GameSearchResults, SearchResult } from "@/search/types.d.mjs";
import clone from "@/util/clone.mjs";
import globals from "@/util/global-whitelist.mjs";

export const EVENT_SHOW_SEARCH = "EVENT_SHOW_SEARCH";
const MAX_RECENT_SEARCH_COUNT = 10;
const MAX_FAVORITES_COUNT = 5;

function sanitizeResult(result: SearchResult) {
  const item = { ...result };
  item.name = item["target"] || item.name; // use base name if available (no mark)
  // strip out the stuff we don't want to save
  delete item["obj"]; // original object
  delete item[0]; // added by fuzzysort
  delete item["searchMeta"]; // search metadata, already saved to the base result
  return clone(item); // second clone to avoid cyclic false positive with shared labels
}

// recents api
export function addRecentResult(result: SearchResult) {
  const recents = [...readState.search.recents];

  if (resultIsInList(result, recents)) return false;

  const item = sanitizeResult(result);
  addResult(item, recents, MAX_RECENT_SEARCH_COUNT);

  writeRecents(recents);
  return true;
}

export function removeRecentResult(result: SearchResult) {
  const recents = [...readState.search.recents];
  removeResult(result, recents);

  writeRecents(recents);
}

export function getRecentSearches() {
  return readData(["search", "recents"], { initializeOnce: true });
}

export function addFavoriteResult(result: SearchResult) {
  const favorites = clone(readState.search.favorites);

  const item = sanitizeResult(result);
  addResult(item, favorites, MAX_FAVORITES_COUNT);

  writeFavorites(favorites.filter(Boolean));
}

export function removeFavoriteResult(result: SearchResult) {
  const favorites = clone(readState.search.favorites);

  removeResult(result, favorites);
  writeFavorites(favorites);
}

export function getFavoriteResults() {
  return readData(["search", "favorites"], { initializeOnce: true });
}

export function showSearch() {
  // special case handling for when there's another search...
  const searchInput = globals.document.querySelector(
    "#search-input",
  ) as HTMLElement;
  if (searchInput) {
    searchInput.focus();
    return;
  }

  eventBus.emit(EVENT_SHOW_SEARCH);
  omniSearchState.show = true;
  // used to clear previous manual game filter
  setSearchGame();
}

export function hideSearch() {
  omniSearchState.show = false;
}

export function setSearchQuery(query = "", inputAttrs = null) {
  searchRefs.globalSearchQuery = query;
  searchRefs.globalSearchInputAttrs = inputAttrs;
}

// updates the state in route to signal to the default route (any page)
// that we need to fetch the static game data for all games
export function prepareSearch() {
  setRouteStateKey(IS_SEARCH_ACTIVE, true);
}

export function setSearchQueryOnRoute(
  { query, game }: SearchDispatch,
  onComplete?: (results: GameSearchResults[]) => void,
) {
  setRouteStateKey(SEARCH_QUERY, {
    query,
    game,
    onComplete,
  });
}

function setRouteStateKey(key, value) {
  if (!router.route) return;
  const {
    route: { state, currentPath, searchParams },
  } = router;
  if (state[key] === value) return;
  updateRoute(currentPath, searchParams, {
    ...state,
    [key]: value,
  });
}

// helpers used for recents and favorites
function findResultIndex(result: SearchResult, arr) {
  return arr
    .filter(Boolean)
    .findIndex((r: SearchResult) => getResultKey(r) === getResultKey(result));
}

function getResultKey(result: SearchResult) {
  const properties = [
    GAME_SHORT_NAMES[result.game],
    result["target"] || result.name,
    result["region"],
    result.tagLine,
  ].filter(Boolean);
  return properties.join(":");
}

export function resultIsInList(result: SearchResult, arr) {
  return findResultIndex(result, arr) !== -1;
}
function removeResult(result: SearchResult, arr) {
  const idx = findResultIndex(result, arr);
  if (idx === -1) return;
  arr.splice(idx, 1);
}
// adds the result to the front of the array, removing any duplicates
function addResult(result: SearchResult, arr, limit) {
  removeResult(result, arr);
  arr.unshift(result);
  if (limit < arr.length) arr.length = limit;
}
function writeRecents(recents: GameSearchResults[]) {
  recents[isPersistent] = MAX_TIME;
  writeState.search.recents = recents;
}
function writeFavorites(favorites: GameSearchResults[]) {
  favorites[isPersistent] = MAX_TIME;
  writeState.search.favorites = favorites;
}
